// src/Dashboard/pages/user/TechniquesQuizzDashboard/components/TechniquesMemorySettingsCard.jsx
// Adapte le chemin si besoin.

import React from "react";
import {
  FaLayerGroup,
  FaRedoAlt,
  FaShieldAlt,
  FaThLarge,
} from "react-icons/fa";

import { DEFAULT_QUIZZ_UI_STATE } from "../utils/techniquesQuizzUtils";

export default function TechniquesMemorySettingsCard({
  ui,
  loading,
  grades = [],
  cardCountOptions = [],
  availableTechniques = 0,
  includeSelfDefense,
  onIncludeSelfDefenseChange,
  onChange,
  onRestart,
}) {
  const selectedGrades = Array.isArray(ui?.grades)
    ? ui.grades
    : [DEFAULT_QUIZZ_UI_STATE.grade];

  const isAllSelected = selectedGrades.includes(DEFAULT_QUIZZ_UI_STATE.grade);

  const toggleGrade = (grade) => {
    if (grade === DEFAULT_QUIZZ_UI_STATE.grade) {
      onChange({
        grades: [DEFAULT_QUIZZ_UI_STATE.grade],
      });
      return;
    }

    const withoutAll = selectedGrades.filter(
      (item) => item !== DEFAULT_QUIZZ_UI_STATE.grade
    );

    const nextGrades = withoutAll.includes(grade)
      ? withoutAll.filter((item) => item !== grade)
      : [...withoutAll, grade];

    onChange({
      grades: nextGrades.length ? nextGrades : [DEFAULT_QUIZZ_UI_STATE.grade],
    });
  };

  const canUseCardCount = (count) => cardCountOptions.includes(count);

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Paramètres du jeu des familles
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Choisir les ceintures et le nombre de cartes
            </h2>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Sélectionnez une ou plusieurs ceintures. Le jeu génère ensuite des
              paires de techniques à retrouver.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
            <span className="text-slate-500 dark:text-slate-400">
              Disponibles :
            </span>{" "}
            <span className="font-bold text-slate-900 dark:text-white">
              {loading ? "…" : availableTechniques}
            </span>
            <span className="text-slate-500 dark:text-slate-400">
              {" "}
              techniques
            </span>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-12">
          <div className="xl:col-span-6">
            <label className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <FaLayerGroup />
              Ceintures
            </label>

            <div className="flex flex-wrap gap-2">
              {grades.map((grade) => {
                const selected =
                  grade === DEFAULT_QUIZZ_UI_STATE.grade
                    ? isAllSelected
                    : selectedGrades.includes(grade);

                return (
                  <button
                    key={grade}
                    type="button"
                    onClick={() => toggleGrade(grade)}
                    disabled={loading}
                    className={`rounded-2xl px-3 py-2 text-xs font-bold ring-1 transition disabled:opacity-60 ${
                      selected
                        ? "bg-[#013E8E] text-white ring-[#013E8E]"
                        : "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100 dark:bg-slate-800/60 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
                    }`}
                  >
                    {grade}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="xl:col-span-3">
            <label className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <FaThLarge />
              Nombre de cartes
            </label>

            <div className="grid grid-cols-3 gap-2">
              {[10, 20, 30].map((count) => {
                const enabled = canUseCardCount(count);
                const selected = Number(ui.cardCount) === Number(count);

                return (
                  <button
                    key={count}
                    type="button"
                    onClick={() => onChange({ cardCount: count })}
                    disabled={loading || !enabled}
                    className={`min-h-[46px] rounded-2xl px-3 text-sm font-bold ring-1 transition disabled:cursor-not-allowed disabled:opacity-40 ${
                      selected
                        ? "bg-[#4CA82F] text-white ring-[#4CA82F]"
                        : "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100 dark:bg-slate-800/60 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
                    }`}
                  >
                    {count}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="xl:col-span-2">
            <label className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <FaShieldAlt />
              Self-défense
            </label>

            <button
              type="button"
              onClick={() => onIncludeSelfDefenseChange(!includeSelfDefense)}
              disabled={loading}
              className={`flex min-h-[46px] w-full items-center justify-between gap-3 rounded-2xl px-4 text-sm font-bold ring-1 transition disabled:opacity-60 ${
                includeSelfDefense
                  ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60"
                  : "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100 dark:bg-slate-800/60 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
              }`}
            >
              <span>{includeSelfDefense ? "Incluse" : "Exclue"}</span>

              <span
                className={`relative inline-flex h-6 w-11 rounded-full transition ${
                  includeSelfDefense
                    ? "bg-emerald-500"
                    : "bg-slate-300 dark:bg-slate-700"
                }`}
              >
                <span
                  className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition ${
                    includeSelfDefense ? "left-6" : "left-1"
                  }`}
                />
              </span>
            </button>
          </div>

          <div className="xl:col-span-1">
            <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Action
            </label>

            <button
              type="button"
              onClick={onRestart}
              disabled={loading}
              className="inline-flex min-h-[46px] w-full items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:opacity-60"
              title="Nouveau jeu"
            >
              <FaRedoAlt />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}